import React, { useEffect, useRef, useState } from "react";
import {
  DollarSign, X, Loader2, PenLine, CreditCard, Banknote, FileCheck2,
  ExternalLink, CheckCircle2, Mail,
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { estimatesApi, invoicesApi } from "../lib/api";
import SignaturePad from "./SignaturePad";

const money = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const METHODS = [
  { key: "card", label: "Card" },
  { key: "check", label: "Check" },
  { key: "cash", label: "Cash" },
];

const METHOD_LABEL = { card: "card", check: "check", cash: "cash" };
const METHOD_ICON = { card: CreditCard, check: FileCheck2, cash: Banknote };

const inputCls =
  "w-full h-10 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400";
const labelCls = "block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1";

/**
 * Unified in-person "Accept payment" popup — used on estimates (Present flow),
 * invoices (admin Accept Payment), and the technician mobile work-order flow.
 * Card → opens the hosted payment page (tenant's own processor) in a new tab
 * and auto-updates here once paid. Check/Cash → recorded with a customer
 * authorization signature.
 */
export default function ProcessPaymentModal({
  kind = "invoice", // "invoice" | "estimate"
  docId,
  docLabel,
  customerName,
  balance,
  defaultAmount,
  onClose,
  onDone,
}) {
  const bal = Number(balance || 0);
  const [amount, setAmount] = useState(Number(defaultAmount ?? bal).toFixed(2));
  const [method, setMethod] = useState("card");
  const [checkNumber, setCheckNumber] = useState("");
  const [note, setNote] = useState("");
  const sigRef = useRef(null);
  const [hasInk, setHasInk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("form"); // form | waiting | done
  const [received, setReceived] = useState(0);
  const baselineRef = useRef(0);
  const pollTimer = useRef(null);
  const doneRef = useRef(false);

  const api = kind === "estimate" ? estimatesApi : invoicesApi;
  const paidOf = (doc) =>
    kind === "estimate" ? Number(doc.depositPaid || 0) : Number(doc.amountPaid || 0);

  const amt = parseFloat(amount) || 0;
  const amountValid = amt > 0 && amt <= bal + 0.005;
  const needsSig = method === "check" || method === "cash";
  const fieldsValid = method === "check" ? !!checkNumber.trim() : true;
  const canSubmit = amountValid && (!needsSig || (fieldsValid && hasInk)) && !busy;

  const payUrl = () => {
    const origin = window.location.origin;
    return kind === "estimate"
      ? `${origin}/pay/deposit/${docId}?amount=${amt.toFixed(2)}`
      : `${origin}/pay/${docId}?amount=${amt.toFixed(2)}`;
  };

  useEffect(() => () => { if (pollTimer.current) clearInterval(pollTimer.current); }, []);

  const startCardFlow = async () => {
    setBusy(true);
    try {
      const doc = await api.get(docId);
      baselineRef.current = paidOf(doc);
    } catch {
      baselineRef.current = 0;
    }
    window.open(payUrl(), "_blank");
    setBusy(false);
    setPhase("waiting");
    pollTimer.current = setInterval(async () => {
      try {
        const doc = await api.get(docId);
        const delta = paidOf(doc) - baselineRef.current;
        if (delta > 0.005 && !doneRef.current) {
          doneRef.current = true;
          clearInterval(pollTimer.current);
          setReceived(delta);
          setPhase("done");
          toast.success(`Payment of ${money(delta)} received`);
          setTimeout(() => onDone(doc, { amount: Math.round(delta * 100) / 100, method: "card" }), 1600);
        }
      } catch {
        /* keep polling */
      }
    }, 3000);
  };

  const cancelWaiting = () => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    setPhase("form");
  };

  const submit = async () => {
    if (!canSubmit) return;
    if (method === "card") {
      startCardFlow();
      return;
    }
    setBusy(true);
    try {
      const payload = {
        amount: amt,
        method,
        note,
        checkNumber: checkNumber.trim(),
        authorization: {
          dataUrl: sigRef.current.toDataURL(),
          signedBy: customerName || "Customer",
        },
      };
      const updated = await api.charge(docId, payload);
      toast.success(`Payment of ${money(amt)} accepted`);
      onDone(updated, { amount: amt, method });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "The payment could not be recorded");
      setBusy(false);
    }
  };

  const MIcon = METHOD_ICON[method];

  return (
    <div
      className="fixed inset-0 z-[140] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center px-4 py-6"
      onClick={busy || phase !== "form" ? undefined : onClose}
      data-testid="process-payment-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3 flex items-start justify-between shrink-0">
          <div>
            <div className="text-[15px] font-bold text-slate-900 flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-emerald-600" />
              Accept payment
            </div>
            <div className="text-[11.5px] text-slate-500 mt-0.5">
              {docLabel || docId} · balance {money(bal)}
              {customerName && ` · ${customerName}`}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="ppay-close"
            className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Waiting for hosted card payment */}
        {phase === "waiting" && (
          <div className="px-5 pb-6 pt-2 flex flex-col items-center text-center" data-testid="ppay-waiting">
            <Loader2 className="h-9 w-9 text-emerald-600 animate-spin mb-3" />
            <div className="text-[15px] font-bold text-slate-900">Waiting for card payment…</div>
            <p className="text-[12.5px] text-slate-500 mt-1.5 max-w-xs">
              The secure payment page opened in a new tab. This popup updates automatically the
              moment the payment of <strong className="text-slate-700">{money(amt)}</strong> is completed.
            </p>
            <div className="mt-2 flex items-center gap-1.5 text-[11.5px] text-slate-400">
              <Mail className="h-3.5 w-3.5" /> A receipt is emailed to the customer after payment.
            </div>
            <div className="mt-5 flex items-center gap-2">
              <Button variant="outline" onClick={cancelWaiting} data-testid="ppay-waiting-cancel" className="h-10 text-[13px]">
                Back
              </Button>
              <Button
                onClick={() => window.open(payUrl(), "_blank")}
                data-testid="ppay-reopen-link"
                className="h-10 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px] font-semibold"
              >
                <ExternalLink className="h-4 w-4 mr-1.5" /> Reopen payment page
              </Button>
            </div>
          </div>
        )}

        {/* Payment received */}
        {phase === "done" && (
          <div className="px-5 pb-8 pt-3 flex flex-col items-center text-center" data-testid="ppay-done">
            <div className="h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            </div>
            <div className="text-[17px] font-bold text-slate-900">Payment received — {money(received)}</div>
            <p className="text-[12.5px] text-slate-500 mt-1">
              Records updated · receipt emailed to the customer.
            </p>
          </div>
        )}

        {phase === "form" && (
          <>
            <div className="px-5 pb-4 space-y-3 overflow-y-auto">
              <div>
                <label className={labelCls}>Amount</label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-[13px]">$</span>
                  <input
                    type="number"
                    min={0.01}
                    max={bal}
                    step={0.01}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    data-testid="ppay-amount"
                    className={`${inputCls} pl-6 font-semibold text-[14px]`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setAmount(bal.toFixed(2))}
                  data-testid="ppay-fill-balance"
                  className="mt-1 text-[11px] text-emerald-700 hover:text-emerald-800 font-medium"
                >
                  Fill balance ({money(bal)})
                </button>
              </div>

              <div>
                <label className={labelCls}>Method</label>
                <select
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  data-testid="ppay-method"
                  className={inputCls}
                >
                  {METHODS.map((m) => (
                    <option key={m.key} value={m.key}>{m.label}</option>
                  ))}
                </select>
              </div>

              {/* Method-specific area */}
              <div
                className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2.5"
                data-testid={`ppay-details-${method}`}
              >
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                  <MIcon className="h-3.5 w-3.5" /> {METHODS.find((m) => m.key === method)?.label} payment
                </div>

                {method === "card" && (
                  <div className="text-[12.5px] text-slate-600 leading-relaxed" data-testid="ppay-card-hint">
                    The customer pays <strong>{money(amt)}</strong> securely on your payment
                    processor's page — it opens in a new tab on this device. This popup updates
                    automatically once the payment completes, and a receipt is emailed to the
                    customer.
                  </div>
                )}

                {method === "check" && (
                  <div>
                    <label className={labelCls}>Check number</label>
                    <input
                      value={checkNumber}
                      onChange={(e) => setCheckNumber(e.target.value)}
                      placeholder="e.g. 1042"
                      data-testid="ppay-check-number"
                      className={inputCls}
                    />
                  </div>
                )}

                {method === "cash" && (
                  <div className="text-[12.5px] text-slate-500" data-testid="ppay-cash-hint">
                    Collect {money(amt)} in cash from the customer — no additional details needed.
                  </div>
                )}
              </div>

              <div>
                <label className={labelCls}>Note</label>
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" data-testid="ppay-note" className={inputCls} />
              </div>

              {/* Payment authorization signature — check/cash only */}
              {needsSig && (
                <div className="pt-1">
                  <div className="flex items-center justify-between mb-1">
                    <label className={`${labelCls} mb-0 flex items-center gap-1.5`}>
                      <PenLine className="h-3.5 w-3.5 text-blue-500" /> Payment authorization
                    </label>
                    <button
                      type="button"
                      onClick={() => sigRef.current?.clear?.()}
                      data-testid="ppay-auth-clear"
                      className="text-[11px] text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Clear
                    </button>
                  </div>
                  <div data-testid="ppay-auth-signature">
                    <SignaturePad ref={sigRef} onChange={setHasInk} height={110} />
                  </div>
                  <p className="mt-1.5 text-[11px] leading-snug text-slate-500" data-testid="ppay-auth-text">
                    By signing above, the customer authorizes a payment of{" "}
                    <strong className="text-slate-700">{money(amt)}</strong> by {METHOD_LABEL[method]} toward{" "}
                    {docLabel || docId}, and confirms the amount shown is correct.
                  </p>
                </div>
              )}
            </div>

            <div className="px-5 py-3.5 border-t border-slate-100 flex items-center justify-end gap-2 shrink-0">
              <Button variant="outline" onClick={onClose} disabled={busy} data-testid="ppay-cancel" className="h-10 text-[13px] disabled:text-white">
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={!canSubmit}
                data-testid="ppay-accept"
                className="h-10 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-500 text-white text-[13px] font-semibold px-5"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <DollarSign className="h-4 w-4 mr-1.5" />}
                Accept Payment
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
